import { ApiClient } from "apis/config/apiClient";
import axios, { AxiosResponse } from "axios";
import { DEFAULT_HEADERS } from "../config/headers";

const SPRING_BACKEND_URL = import.meta.env.VITE_SPRING_BACKEND_URL;

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  message: string;
  status: string;
  data?: {
    email: string;
    name: string | null;
    accessToken: string;
  };
}

// 회원가입 관련 인터페이스들
export interface SignupData {
  id: {
    email: string;
    token: string;
  };
  profile: {
    name: string;
  };
  password: string;
  company: {
    name: string;
    industry: {
      code: string;
      other: string | null;
    };
    size: {
      code: string;
      other: string | null;
    };
    jobPosition: {
      code: string;
      other: string | null;
    };
    jobDuties: {
      code: string;
      other: string | null;
    };
  };
  consent: {
    isPrivacy: boolean;
    isMarketing: boolean;
  };
}

interface EmailCheckResponse {
  status: number;
  message?: string;
}

interface EmailVerificationResponse {
  message: string;
  data?: {
    token: string;
  };
}

interface SignupResponse {
  status: number;
  message?: string;
}

// 비밀번호 재설정 관련 인터페이스들
interface PasswordResetData {
  authToken: string;
  password: string;
}

interface PasswordResetResponse {
  status: number;
  message?: string;
}

// Login API call
export const loginUser = async (
  credentials: LoginCredentials
): Promise<LoginResponse> => {
  try {
    const response: AxiosResponse<LoginResponse> = await axios.post(
      `${SPRING_BACKEND_URL}/auth/login`,
      credentials,
      {
        headers: DEFAULT_HEADERS,
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during login request:", error);
    throw error;
  }
};

export const logoutUser = async (
  accessToken: string
): Promise<LoginResponse> => {
  try {
    const response: AxiosResponse<LoginResponse> = await ApiClient.getInstance()
      .getAxiosInstance()
      .post(
        `${SPRING_BACKEND_URL}/auth/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
    return response.data;
  } catch (error) {
    console.error("Error during logout request:", error);
    throw error;
  }
};

// 회원가입 관련 API 함수들

// 이메일 중복 체크 및 인증코드 전송
export const checkEmailDuplication = async (
  email: string
): Promise<EmailCheckResponse> => {
  try {
    const response: AxiosResponse<EmailCheckResponse> = await axios.get(
      `${SPRING_BACKEND_URL}/auth/email`,
      {
        params: {
          type: "signup",
          email,
        },
        headers: DEFAULT_HEADERS,
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during email duplication check:", error);
    throw error;
  }
};

// 이메일 인증 토큰 확인
export const verifyEmailToken = async (
  email: string,
  code: string,
  type: string
): Promise<EmailVerificationResponse> => {
  try {
    const response: AxiosResponse<EmailVerificationResponse> = await axios.get(
      `${SPRING_BACKEND_URL}/auth/email/token`,
      {
        headers: DEFAULT_HEADERS,
        params: {
          type: type,
          email,
          code,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during email verification:", error);
    throw error;
  }
};

// 회원가입
export const signupUser = async (
  signupData: SignupData
): Promise<SignupResponse> => {
  try {
    const response: AxiosResponse<SignupResponse> = await axios.post(
      `${SPRING_BACKEND_URL}/me/join`,
      signupData,
      {
        headers: DEFAULT_HEADERS,
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during signup request:", error);
    throw error;
  }
};

// 비밀번호 찾기 관련 API 함수들

// 비밀번호 재설정 링크 전송
export const sendPasswordResetLink = async (
  email: string
): Promise<EmailCheckResponse> => {
  try {
    const response: AxiosResponse<EmailCheckResponse> = await axios.get(
      `${SPRING_BACKEND_URL}/auth/email`,
      {
        params: {
          type: "changePassword",
          email,
        },
        headers: DEFAULT_HEADERS,
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during password reset link send:", error);
    throw error;
  }
};

// 비밀번호 재설정
export const resetPassword = async (
  resetData: PasswordResetData
): Promise<PasswordResetResponse> => {
  try {
    console.log('API call - resetData:', resetData); // 디버깅용
    console.log('API URL:', `${SPRING_BACKEND_URL}/auth/password/token`); // 디버깅용
    
    const response: AxiosResponse<PasswordResetResponse> = await axios.patch(
      `${SPRING_BACKEND_URL}/auth/password/token`,
      resetData,
      {
        headers: DEFAULT_HEADERS,
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during password reset:", error);
    throw error;
  }
};
